import type { AiCapabilityPage } from "../schema.js";
import { LIVE, OWNER_ADMIN, SLOT_GROUP, WRITE_FLAGS } from "./_shared.js";

/**
 * `ONB-CREATE-FUND` (@UC0193) — thêm một quỹ cho nhóm, T1. Executor = `FundsService.createFund` (funds.service.ts:425); GUI
 * `POST /groups/:id/funds` gác `@RequireGroupRoles(OWNER, ADMIN)` (funds.controller.ts:191) ⇒ vai trang = O/A, KHÔNG thủ quỹ (review #811 H1).
 *
 * 🔴 ĐÂY LÀ QUỸ **THÊM**, KHÔNG PHẢI QUỸ ĐẦU TIÊN (QĐ PO 17/09 09:20 — đừng "sửa lại" câu chữ về nghĩa tạo mới):
 * `POST /v1/groups` đã tạo sẵn một quỹ mặc định **"Quỹ chung"** trong CÙNG tx lúc lập nhóm (`groups.service.ts:406`),
 * nên mọi nhóm đều đã có ít nhất một quỹ. Trang này phục vụ người muốn TÁCH sổ: quỹ sự kiện, quỹ dự phòng… Câu chữ vì
 * thế nói "thêm một quỹ nữa", không nói "tạo quỹ cho nhóm" — hứa sai ở đây làm người dùng tưởng nhóm chưa có quỹ nào.
 *
 * 🔴 KHÔNG CÓ Ô "LOẠI QUỸ" — và đây là quyết định ĐO ĐƯỢC, không phải quên (0032 S03, xem int-spec `@UC0193` ca "cửa GUI").
 * Bản S02 từng khai ô enum `loaiQuy` (quỹ chung/sự kiện/dự phòng) → `FundType`. Đo lại ở CHÍNH cửa dùng chung
 * (`createFundRequestSchema.superRefine`, `packages/contracts/src/funds.ts:339`) thì body `{name, type}` **KHÔNG hợp lệ**:
 *   recurring ⇒ đòi `feeMinor`+`cycle`+`closeDay` · goal ⇒ đòi `targetMinor` · oneoff ⇒ đòi `totalMinor`+`splitMode`.
 * Hai lối duy nhất đều xấu: (a) hỏi luôn sub-config ⇒ trang HỎI TIỀN, đúng thứ trang này từ chối (mức đóng thuộc ĐỢT THU)
 * và trái khung PO "chưa mở đợt thu, chưa ghi tiền"; (b) gửi `type` trần thẳng vào `createFundInTx` ⇒ ghi được một trạng
 * thái mà **form KHÔNG tạo nổi** (quỹ có loại, 0 cấu hình) ⇒ cửa thứ hai. Nên chat gửi ĐÚNG `{name}` — cùng body mà chính
 * app dùng cho quỹ mặc định "Quỹ chung" (`createFundInTx(tx, {groupId, name, notifyMembers:false})`, 0 `type`).
 * ⚠️ Cái MẤT, ghi rõ để không ai tưởng là miễn phí: `type` chỉ đặt được LÚC TẠO (`fundConfigPatchSchema` không có `type`,
 * không route nào khác sửa) ⇒ quỹ tạo bằng chat vĩnh viễn không loại, như quỹ mặc định của mọi nhóm hôm nay.
 * 🟢 Nhưng cái MẤT đó NHỎ HƠN vẻ ngoài (reviewer #836 đo lại): quỹ mặc định `Quỹ chung` của MỌI nhóm cũng `type=null`
 * (`groups.service.ts:406`), và `apps/mobile` có **0 caller** `POST /groups/:id/funds` ⇒ trang này là **thuần cộng thêm,
 * 0 regress** — nó không lấy đi của ai một năng lực đang có. Địa chỉ của phần còn thiếu: **Sổ chưa có trang CẤU HÌNH QUỸ**
 * (đặt loại + sub-config sau khi tạo); khi trang đó có, quỹ tạo bằng chat mới đặt được loại. Điều phối mở thẻ.
 *
 * Kết quả `done` (0005, @UC0364) mời `ONB-ADD-MEMBERS` + `COL-EQUAL`. Đo 17/09 sau khi #832 merge (`3dd8c2a4e`): `COL-EQUAL` = **bound**,
 * `ONB-ADD-MEMBERS` = **draft** (task 0033) ⇒ sau khi thêm quỹ, lượt `done` có **đúng một** nút tĩnh (`COL-EQUAL`); trang
 * draft bị `allowedPages` lọc, không hiện nút chết. Sổ khai **ĐÍCH**, không khai **trạng thái đích**.
 */
export const ONB_CREATE_FUND = {
  id: "ONB-CREATE-FUND",
  uc: "@UC0193",
  tier: "T1",
  status: LIVE,
  roles: OWNER_ADMIN,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_GROUP,
    { id: "tenQuy", type: "text", required: true, ask: "Quỹ mới tên gì? (ví dụ: Quỹ du lịch hè 2027)" },
  ],
  llm: {
    description: "Thêm một quỹ nữa cho nhóm (ngoài Quỹ chung sẵn có) để tách sổ theo việc — chỉ đặt tên; chưa mở đợt thu, chưa ghi tiền.",
    examples: [
      "thêm quỹ cho nhóm",
      "them quy cho nhom",
      "tạo quỹ cho nhóm",
      "tao quy cho nhom",
      "lập quỹ lớp năm học mới",
      "lap quy lop",
      "mở quỹ sự kiện cuối năm",
      "mo quy su kien",
      "tạo quỹ",
      "thêm quỹ dự phòng cho đội",
      "them quy du phong",
      "nhóm mình cần một quỹ chung",
      "tạo quỹ chung",
      "add quỹ mới",
      // ——— 0105: thêm cho đủ 40 ———
      "add quy moi",
      "them quy",
      "tách riêng một quỹ cho chuyến đi",
      "tach rieng mot quy cho chuyen di",
      "mình muốn có thêm một sổ quỹ nữa",
      "minh muon co them mot so quy nua",
      "thêm quỹ du lịch hè",
      "them quy du lich he",
      "tạo thêm quỹ liên hoan cuối năm",
      "tao them quy lien hoan cuoi nam",
      "nhóm này cần thêm quỹ riêng cho giải bóng",
      "nhom nay can them quy rieng cho giai bong",
      "mở thêm một quỹ nữa",
      "mo them mot quy nua",
      "tạo quỹ khen thưởng",
      "tao quy khen thuong",
      "thêm một sổ quỹ tên Quỹ dự phòng",
      "them mot so quy ten Quy du phong",
      "tách quỹ sự kiện ra khỏi quỹ chung",
      "tach quy su kien ra khoi quy chung",
      "thêm quỹ cho nhóm kiểu gì?",
      "them quy cho nhom kieu gi",
      "lập thêm quỹ học bổng cho lớp",
      "lap them quy hoc bong cho lop",
      "tạo quỹ mới tên Quỹ dã ngoại",
      "tao quy moi ten Quy da ngoai",
    ],
  },
  executor: "funds.createFund",
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: mobile CHƯA có màn tạo quỹ thêm (0 caller `POST /groups/:id/funds` trong `apps/mobile/lib`) — trang này là việc AI làm được mà GUI chưa có. */
  endpoints: [],
  facts: ["tenNhom"],
  templates: {
    confirm: "Thêm quỹ “{tenQuy}” cho nhóm {tenNhom}?",
    done: "Đã thêm quỹ “{tenQuy}”. Nhóm giờ có thêm một sổ riêng cho việc này.",
  },
  journey: { outcomes: { done: { next: ["ONB-ADD-MEMBERS", "COL-EQUAL"] } } },
  help: { title: "Thêm quỹ cho nhóm", tryNow: "thêm quỹ cho nhóm" },
} satisfies AiCapabilityPage;
